import { skipToken } from "@reduxjs/toolkit/query";
import {
  copilotProposalIdSchema,
  type CopilotProposalId,
} from "@repo/app-types/entities/copilotProposal";
import { getOutcomeValueAndLabel } from "@repo/app-types/entities/outcome";
import { getOutcomeDefinitionFromPlaybook } from "@repo/app-types/entities/playbook";
import {
  PlaybookTestResultStatus,
  type AdminApiPlaybookTestResult,
  type AdminApiPlaybookTestSpec,
  type AdminApiPlaybookTestSpecResult,
} from "@repo/app-types/entities/teammateAgent";
import { checkPlaybookTestOutcomeMatch } from "@repo/app-types/helpers/playbookTestVerdict";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import {
  IconCheck,
  IconEye,
  IconPencil,
  IconPlayerPlay,
  IconPlus,
  IconRefresh,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { format } from "date-fns";
import { useEffect, useMemo, useState, type FC } from "react";
import { useParams, useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import {
  useAllPlaybookTestSpecsQuery,
  useCreatePlaybookTestSpecMutation,
  useDeletePlaybookTestSpecMutation,
  useUpdatePlaybookTestSpecMutation,
} from "../../api/entities/playbookTestSpec.js";
import {
  useCreateTeammateAgentTestRunsMutation,
  useGetTeammateAgentQuery,
  useGetTeammateAgentTestSpecsResultsQuery,
} from "../../api/entities/teammateAgent.js";
import { PageBody } from "../../components/layout/pageLayout.js";
import { readPlaybookTestResultStatus } from "../../components/recordingAssessmentEditor/feedbackAccuracyHelpers.js";
import { useAppDispatch } from "../../hooks/hook.js";
import {
  NavigationSlideoverContainer,
  useModalNavigation,
} from "../../hooks/useModalNavigation.js";
import { useWaitForAgentPrompt } from "../../hooks/useWaitForAgentPrompt.js";
import { COPILOT_PROPOSAL_QUERY_PARAM } from "../copilot/copilotDeepLinks.js";
import { useReportEditorProposal } from "../copilot/editorProposalsSlice.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";
import {
  PlaybookTestSpecEditorSlideover,
  type PlaybookTestSpecDraft,
} from "./playbookTestSpecEditorSlideover.js";
import { describePlaybookTestVerdictTallies } from "./playbookTestVerdictSummary.js";
import { TEST_SPEC_QUERY_PARAM } from "./teammateAgentProposalCardContent.js";

/**
 * The test case an Ask Alpharun card just applied, from the one-shot
 * `?proposalId=` and `?testSpecId=` params: highlighted in the table and
 * reported to the chat as viewed until the user edits or adds a case.
 */
interface ViewedTestCase {
  proposalId: CopilotProposalId;
  testSpecId: string | null;
}

export const TeammateAgentTestingTab: FC = () => {
  const { teammateAgentId } = useParams();
  if (!teammateAgentId) {
    throw new Error("Missing params.");
  }

  const dispatch = useAppDispatch();
  const modalNavigation = useModalNavigation();

  const { data: teammateAgent, isLoading: isLoadingTeammateAgent } =
    useGetTeammateAgentQuery(teammateAgentId);

  const playbookId = teammateAgent?.teammate_agent_config.playbook_id;

  // Fetch the playbook to get node types for comparison
  const { data: playbook } = usePlaybookByIdQuery(playbookId ?? skipToken);

  // Fetch all test specs for the playbook
  const { data: allTestSpecs, isLoading: isLoadingTestSpecs } =
    useAllPlaybookTestSpecsQuery(playbookId ? { playbookId } : skipToken);

  // Track if there are in-progress tests to enable polling
  const [hasInProgressTests, setHasInProgressTests] = useState(false);

  // Fetch test results for specs that have been run
  const {
    data: testSpecsResults,
    isLoading: isLoadingTestSpecsResults,
    refetch: refetchTestSpecsResults,
  } = useGetTeammateAgentTestSpecsResultsQuery(
    playbookId ? { teammateAgentId, playbookId } : skipToken,
    { pollingInterval: hasInProgressTests ? 5000 : 0 },
  );

  const [createPlaybookTestSpec] = useCreatePlaybookTestSpecMutation();
  const [updatePlaybookTestSpec] = useUpdatePlaybookTestSpecMutation();
  const [deletePlaybookTestSpec] = useDeletePlaybookTestSpecMutation();
  const [createTeammateAgentTestRuns] =
    useCreateTeammateAgentTestRunsMutation();

  const { waitForPromptReady } = useWaitForAgentPrompt({ teammateAgentId });

  const [searchParams, setSearchParams] = useSearchParams();
  const [viewedTestCase, setViewedTestCase] = useState<ViewedTestCase | null>(
    () => {
      const proposalId = copilotProposalIdSchema.safeParse(
        searchParams.get(COPILOT_PROPOSAL_QUERY_PARAM),
      ).data;
      return proposalId
        ? { proposalId, testSpecId: searchParams.get(TEST_SPEC_QUERY_PARAM) }
        : null;
    },
  );
  useEffect(() => {
    if (
      searchParams.has(COPILOT_PROPOSAL_QUERY_PARAM) ||
      searchParams.has(TEST_SPEC_QUERY_PARAM)
    ) {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(COPILOT_PROPOSAL_QUERY_PARAM);
          next.delete(TEST_SPEC_QUERY_PARAM);
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);
  useReportEditorProposal(viewedTestCase?.proposalId, false);

  const [isSlideoverOpen, setIsSlideoverOpen] = useState(false);
  const [editingTestSpec, setEditingTestSpec] =
    useState<AdminApiPlaybookTestSpec | null>(null);
  const [testSpecToDelete, setTestSpecToDelete] = useState<string | null>(null);
  const [runningTestIds, setRunningTestIds] = useState<Set<string>>(new Set());
  const [isRunningAllTests, setIsRunningAllTests] = useState(false);

  const handleSubmitTestSpec = async (
    draft: PlaybookTestSpecDraft,
    testSpecId?: string,
  ) => {
    if (!playbookId) {
      return;
    }

    try {
      let finalTestSpecId: string;

      if (testSpecId) {
        // Update existing test spec
        await updatePlaybookTestSpec({
          playbookId,
          testSpecId,
          payload: draft,
        }).unwrap();
        finalTestSpecId = testSpecId;
      } else {
        // Create new test spec
        const createdTestSpec = await createPlaybookTestSpec({
          playbookId,
          payload: draft,
        }).unwrap();
        finalTestSpecId = createdTestSpec.id;
      }

      // Wait for any pending prompt regeneration to complete before running the test
      const promptReady = await waitForPromptReady();
      if (!promptReady) {
        showNotification(dispatch, {
          id: `prompt-timeout-${Date.now()}`,
          type: NotificationType.FAILURE,
          primaryMessage: "Agent prompt still generating",
          secondaryMessage:
            "Please wait a moment for the agent to finish updating and try again.",
        });
        setIsSlideoverOpen(false);
        setEditingTestSpec(null);
        return;
      }

      // Automatically trigger a test run for the test spec
      await createTeammateAgentTestRuns({
        teammateAgentId,
        playbookTestSpecIds: [finalTestSpecId],
      }).unwrap();

      setIsSlideoverOpen(false);
      setEditingTestSpec(null);
    } catch (error) {
      console.error("Failed to save test spec or trigger test run:", error);
      showNotification(dispatch, {
        id: `test-spec-error-${Date.now()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to save test case",
        secondaryMessage: "Please try again.",
      });
    }
  };

  // Merge all test specs with their results (if they have been run)
  const mergedTestSpecsResults = useMemo<
    AdminApiPlaybookTestSpecResult[]
  >(() => {
    if (!allTestSpecs) {
      return [];
    }

    // Create a map of test spec ID to result for quick lookup
    const resultsMap = new Map<string, AdminApiPlaybookTestSpecResult>();
    testSpecsResults?.forEach((result) => {
      resultsMap.set(result.playbook_test_spec.id, result);
    });

    // Transform all test specs into AdminApiPlaybookTestSpecResult format
    return allTestSpecs.map((testSpec) => {
      // If we have a result for this test spec, use it
      const existingResult = resultsMap.get(testSpec.id);
      if (existingResult) {
        return existingResult;
      }

      // Otherwise, create a placeholder result structure
      return {
        id: testSpec.id,
        playbook_test_spec: testSpec,
      };
    });
  }, [allTestSpecs, testSpecsResults]);

  const sortedTestSpecsResults = mergedTestSpecsResults.toSorted((a, b) =>
    a.playbook_test_spec.name.localeCompare(b.playbook_test_spec.name),
  );

  // Sync hasInProgressTests state to enable/disable RTK Query polling
  useEffect(() => {
    const hasInProgress = sortedTestSpecsResults.some(
      (testCase) =>
        testCase.playbook_test_result?.status ===
        PlaybookTestResultStatus.IN_PROGRESS,
    );
    setHasInProgressTests(hasInProgress);
  }, [sortedTestSpecsResults]);

  const columns = useMemo<
    EntityTableColumnDefinition<AdminApiPlaybookTestSpecResult>[]
  >(() => {
    const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

    // Whether the run's outcome is the one the case expects, with a tooltip
    // showing both. It says nothing about the rest of the verdict: an assertion
    // that failed is the status pill's and the tallies' to report.
    const renderOutcomeIndicator = (
      spec: AdminApiPlaybookTestSpec,
      result: AdminApiPlaybookTestResult,
    ) => {
      if (!outcomeDefinition || !result.output) {
        return null;
      }

      const { value: expectedValue, label: expectedLabel } =
        getOutcomeValueAndLabel(spec.expected.outcomes, outcomeDefinition);
      const { label: actualLabel } = getOutcomeValueAndLabel(
        result.output.outcomes,
        outcomeDefinition,
      );

      // No expected outcome = nothing to show
      if (expectedValue === undefined) {
        return null;
      }

      const outcomeMatches = checkPlaybookTestOutcomeMatch(
        spec.expected.outcomes,
        result.output.outcomes,
        outcomeDefinition,
      );

      return (
        <TooltipTrigger>
          <TooltipHoverTarget shape="rounded">
            {outcomeMatches ? (
              <IconCheck className="size-5 text-success" />
            ) : (
              <IconX className="size-5 text-destructive" />
            )}
          </TooltipHoverTarget>
          <SimpleTooltip>
            Expected outcome: {expectedLabel}
            <br />
            Result outcome: {actualLabel}
          </SimpleTooltip>
        </TooltipTrigger>
      );
    };

    // The tallies the stored verdict carries, for a run that was judged.
    const renderVerdictTallies = (result: AdminApiPlaybookTestResult) => {
      const verdict = result.output?.verdict;
      if (!verdict) {
        return null;
      }

      const tallies = describePlaybookTestVerdictTallies(
        verdict,
        result.output?.assertion_verdicts ?? [],
      );
      if (!tallies) {
        return null;
      }

      return <div className="text-xs text-muted-foreground">{tallies}</div>;
    };

    return [
      {
        id: "test-case",
        header: "Test case",
        isRowHeader: true,
        renderCell: (testCase) => {
          const isViewed =
            viewedTestCase?.testSpecId === testCase.playbook_test_spec.id;
          return (
            <div
              className={twMerge(
                "group relative flex items-center justify-between",
                isViewed && "-mx-2 rounded-md bg-indigo-50/60 px-2 py-1",
              )}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  {testCase.playbook_test_spec.name}
                </span>
                <span className="mt-1 text-xs text-muted-foreground">
                  {testCase.playbook_test_spec.description}
                </span>
              </div>
              {isViewed ? (
                <span className="inline-flex shrink-0 items-center gap-1 text-xs text-indigo-700">
                  <IconEye aria-hidden className="size-3.5" />
                  Viewing
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        id: "status",
        header: "Test result",
        renderCell: (testCase) => {
          const { playbook_test_spec: spec, playbook_test_run: run } = testCase;

          // Use the same helper as testStatusPill to determine status
          const status = readPlaybookTestResultStatus(
            spec,
            testCase.playbook_test_result,
            outcomeDefinition,
          );

          switch (status) {
            case "not_run":
              return (
                <span className="mt-1 text-xs text-muted-foreground">
                  Test case not executed yet
                </span>
              );
            case "in_progress":
              return (
                <div className="flex items-center gap-2">
                  <div className="-mx-1">
                    <TooltipTrigger>
                      <TooltipHoverTarget shape="rounded">
                        <Loader style={LoaderStyle.SPINNER} />
                      </TooltipHoverTarget>
                      <SimpleTooltip>{`Started on ${run ? format(new Date(run.created_at), "PP p") : "unknown"}`}</SimpleTooltip>
                    </TooltipTrigger>
                  </div>
                  <div className="text-xs whitespace-nowrap text-muted-foreground">
                    Running test...
                  </div>
                </div>
              );
            case "failing": {
              const result = testCase.playbook_test_result;
              // Check if it's an error vs outcome mismatch
              if (result?.status === PlaybookTestResultStatus.ERROR) {
                return (
                  <TooltipTrigger>
                    <TooltipHoverTarget className="mt-1 text-xs text-muted-foreground">
                      An error occurred
                    </TooltipHoverTarget>
                    <SimpleTooltip>{`Ran on ${run ? format(new Date(run.created_at), "PP p") : "unknown"}`}</SimpleTooltip>
                  </TooltipTrigger>
                );
              }
              // The verdict says it failed - show X indicator
              return (
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    {result && renderOutcomeIndicator(spec, result)}
                    <div className="text-xs whitespace-nowrap text-muted-foreground">
                      {run && format(new Date(run.created_at), "PP p")}
                    </div>
                  </div>
                  {result && renderVerdictTallies(result)}
                </div>
              );
            }
            case "passing": {
              const result = testCase.playbook_test_result;
              return (
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    {result && renderOutcomeIndicator(spec, result)}
                    <div className="text-xs whitespace-nowrap text-muted-foreground">
                      {run && format(new Date(run.created_at), "PP p")}
                    </div>
                  </div>
                  {result && renderVerdictTallies(result)}
                </div>
              );
            }
          }
        },
      },
      {
        id: "actions",
        header: "",
        renderCell: (testCase) => (
          <div className="flex items-center gap-2">
            <IconButton
              variant={ButtonVariant.Secondary}
              icon={<IconRefresh className="h-4 w-4" />}
              isDisabled={runningTestIds.has(testCase.playbook_test_spec.id)}
              onClick={async () => {
                const testSpecId = testCase.playbook_test_spec.id;
                setRunningTestIds((prev) => new Set(prev).add(testSpecId));
                try {
                  // Wait for any pending prompt regeneration to complete
                  const promptReady = await waitForPromptReady();
                  if (!promptReady) {
                    showNotification(dispatch, {
                      id: `prompt-timeout-${testSpecId}-${Date.now()}`,
                      type: NotificationType.FAILURE,
                      primaryMessage: "Agent prompt still generating",
                      secondaryMessage:
                        "Please wait a moment for the agent to finish updating and try again.",
                    });
                    return;
                  }

                  await createTeammateAgentTestRuns({
                    teammateAgentId,
                    playbookTestSpecIds: [testSpecId],
                  }).unwrap();
                  showNotification(dispatch, {
                    id: `test-run-${testSpecId}-${Date.now()}`,
                    type: NotificationType.SUCCESS,
                    primaryMessage: "Test started",
                    secondaryMessage: `This can take a few minutes to complete.`,
                  });
                  // Refetch to show the in_progress status
                  refetchTestSpecsResults();
                } catch (error) {
                  showNotification(dispatch, {
                    id: `test-run-error-${testSpecId}-${Date.now()}`,
                    type: NotificationType.FAILURE,
                    primaryMessage: "Failed to start test",
                    secondaryMessage: "Please try again.",
                  });
                } finally {
                  setRunningTestIds((prev) => {
                    const next = new Set(prev);
                    next.delete(testSpecId);
                    return next;
                  });
                }
              }}
            />
            <IconButton
              variant={ButtonVariant.Secondary}
              icon={<IconPencil className="h-4 w-4" />}
              onClick={() => {
                setViewedTestCase(null);
                setEditingTestSpec(testCase.playbook_test_spec);
                setIsSlideoverOpen(true);
              }}
            />
            <IconButton
              variant={ButtonVariant.Secondary}
              icon={<IconTrash className="h-4 w-4" />}
              onClick={() => {
                setTestSpecToDelete(testCase.playbook_test_spec.id);
              }}
            />
          </div>
        ),
      },
    ];
  }, [
    dispatch,
    teammateAgentId,
    createTeammateAgentTestRuns,
    playbook,
    refetchTestSpecsResults,
    runningTestIds,
    viewedTestCase,
    waitForPromptReady,
  ]);

  return (
    <PageBody width={PageWidth.Wide}>
      <div className="flex flex-wrap justify-between gap-4">
        {/* <div className="rounded-lg border border-border-light bg-white px-4 py-2">
          <FeedbackAccuracySummary
            accuracyData={{
              total: 5,
              correct: 3,
              incorrect: 1,
              close: 1,
              accuracy: 0,
            }}
          />
        </div> */}
        <div className="flex flex-row gap-2">
          <Button
            variant={ButtonVariant.Secondary}
            onClick={async () => {
              if (!allTestSpecs || allTestSpecs.length === 0) {
                return;
              }
              setIsRunningAllTests(true);
              try {
                // Wait for any pending prompt regeneration to complete
                const promptReady = await waitForPromptReady();
                if (!promptReady) {
                  showNotification(dispatch, {
                    id: `prompt-timeout-all-${Date.now()}`,
                    type: NotificationType.FAILURE,
                    primaryMessage: "Agent prompt still generating",
                    secondaryMessage:
                      "Please wait a moment for the agent to finish updating and try again.",
                  });
                  return;
                }

                const allTestSpecIds = allTestSpecs.map((spec) => spec.id);
                await createTeammateAgentTestRuns({
                  teammateAgentId,
                  playbookTestSpecIds: allTestSpecIds,
                }).unwrap();
                showNotification(dispatch, {
                  id: `test-run-all-${Date.now()}`,
                  type: NotificationType.SUCCESS,
                  primaryMessage: "All tests started",
                  secondaryMessage: `This can take a few minutes to complete.`,
                });
                // Refetch to show the in_progress status
                refetchTestSpecsResults();
              } catch (error) {
                showNotification(dispatch, {
                  id: `test-run-all-error-${Date.now()}`,
                  type: NotificationType.FAILURE,
                  primaryMessage: "Failed to start tests",
                  secondaryMessage: "Please try again.",
                });
              } finally {
                setIsRunningAllTests(false);
              }
            }}
            icon={<IconPlayerPlay className="h-4 w-4" />}
            isDisabled={
              !allTestSpecs || allTestSpecs.length === 0 || isRunningAllTests
            }
            isLoading={isRunningAllTests}
          >
            Run all tests
          </Button>
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => {
              setViewedTestCase(null);
              setEditingTestSpec(null);
              setIsSlideoverOpen(true);
            }}
            icon={<IconPlus className="h-4 w-4" />}
          >
            Add test case
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-4 md:flex-row">
        <EntityTable
          aria-label="Agent test cases"
          columns={columns}
          entities={sortedTestSpecsResults}
          isLoading={
            isLoadingTeammateAgent ||
            isLoadingTestSpecs ||
            isLoadingTestSpecsResults
          }
          emptyMessage="No test cases have been created yet."
          onEntityAction={(testCase) => {
            const testSpecId = testCase.playbook_test_spec.id;
            const testRunId = testCase.playbook_test_run?.id;
            const testResultId = testCase.playbook_test_result?.id;
            if (testResultId && testRunId) {
              modalNavigation.push(
                `specs/${encodeURIComponent(testSpecId)}/runs/${encodeURIComponent(testRunId)}/results/${encodeURIComponent(testResultId)}/assessment`,
              );
            }
          }}
        />
      </div>

      <NavigationSlideoverContainer />

      <SlideoverContainer
        size={Size.SMALL}
        isDismissable
        isOpen={isSlideoverOpen}
        onOpenChange={(newIsOpen) => {
          if (!newIsOpen) {
            setIsSlideoverOpen(false);
            setEditingTestSpec(null);
          }
        }}
      >
        {playbookId && isSlideoverOpen ? (
          <PlaybookTestSpecEditorSlideover
            playbookId={playbookId}
            existingTestSpec={editingTestSpec ?? undefined}
            onClose={() => {
              setIsSlideoverOpen(false);
              setEditingTestSpec(null);
            }}
            onSubmit={handleSubmitTestSpec}
          />
        ) : null}
      </SlideoverContainer>
      <SimpleModal
        isOpen={Boolean(testSpecToDelete)}
        onCancel={() => {
          setTestSpecToDelete(null);
        }}
        variant={SimpleModalVariant.Warning}
        title="Delete test case"
        subtitle="Are you sure you want to delete this test case?"
        confirmButtonText="Delete test case"
        onConfirm={() => {
          if (testSpecToDelete && playbookId) {
            deletePlaybookTestSpec({
              playbookId,
              testSpecId: testSpecToDelete,
            });
          }
          setTestSpecToDelete(null);
        }}
      />
    </PageBody>
  );
};
